<template>
  <div class="billing-data">
    <section class="usage-profile">
      <div>
        <h2 class="section-title">Como você utilizará o HO Fácil Web?</h2>
        <p class="section-subtitle">
          Escolha a opção que melhor descreve sua finalidade de uso. Isso define o regime jurídico aplicável à sua contratação.
        </p>
      </div>

      <div class='btn-radio w-100 d-flex flex-wrap justify-content-between'>
        <button
          @click="billingData.usageProfile = 'professional'"
          :class="billingData.usageProfile === 'professional' ? 'hits_select ':'hits_no_select'"
        >
          <span>Pagar como PROFISSIONAL OU EMPRESA</span>
        </button>

        <button
          @click="billingData.usageProfile = 'consumer'"
          :class="billingData.usageProfile === 'consumer' ? 'hits_select ':'hits_no_select'"
        >
          <span>Pagar como CONSUMIDOR(A)</span>
        </button>
      </div>

      <div class="usage-descriptions d-flex flex-wrap justify-content-between">
        <p>Pessoa jurídica ou física em atividade profissional (SST, SESMT, etc).</p>
        <p>Pessoa física para uso próprio, fora de atividade empresarial.</p>
      </div>

      <hr class="usage-divider" />

      <div class="usage-note">
        <p>Essa declaração define o regime jurídico aplicável à sua contratação e não afasta nenhum direito garantido por lei.</p>
      </div>
    </section>

    <h2 class="section-title mt-4">Dados de Faturamento e Nota Fiscal</h2>
    <p class="billing-intro">
      No sistema HO Fácil Web, todos os usuários são cadastrados como pessoas físicas. No entanto,
      para pagamento e emissão de nota fiscal, você pode escolher a opção que melhor se encaixa
      para gerar boleto ou fatura e nota fiscal. Escolha abaixo a opção desejada para preencher
      os dados de faturamento e nota fiscal:
    </p>

    <div class="mt-2">
      <div class='btn-radio w-100 d-flex flex-wrap justify-content-between'>
        <button
          @click="clearFieldsSwitchBetweenLegalEntity('person')"
          :class="billingData.type === 'person' ? 'hits_select ':'hits_no_select'"
        >
          <span>Pagar como PESSOA FÍSICA (CPF)</span>
        </button>

        <button
          @click="clearFieldsSwitchBetweenLegalEntity('company')"
          :class="billingData.type === 'company' ? 'hits_select ':'hits_no_select'"
        >
          <span>Pagar como PESSOA JURÍDICA (CNPJ)</span>
        </button>
      </div>
    </div>

    <div v-if="billingData.type === 'person'" class="mt-2">
      <label>Nome do usuário</label>
      <base-input-text
        v-model="v$.billingData.name.$model"
        type="text"
        placeholder="Digite o nome da pessoa que usará o sistema"
        block
        :error="getError(v$.billingData.name)"
      />
    </div>

    <div v-if="billingData.type === 'person'" class="mt-2 cpf-data_nascimento_telefone">
      <div>
        <label>CPF</label>
        <base-input-text
          v-model="v$.billingData.document.$model"
          type="text"
          placeholder="Digite o CPF"
          v-mask="['###.###.###-##']"
          class="w-100"
          :error="getError(v$.billingData.document)"
        />
      </div>

      <div>
        <label>Data de Nascimento</label>
        <base-input-text
          v-model="v$.billingData.birthDate.$model"
          type="date"
          placeholder="Digite a data de Nascimento"
          class="w-100"
          :error="getError(v$.billingData.birthDate)"
        />
      </div>

      <div>
        <label>Telefone (WhatsApp)</label>
        <div class="w-100">
          <b-form-input
            id="input-phone"
            v-model="v$.billingData.phone.$model"
            type="text"
            class='input-cpf'
            style="height: 48px;"
            placeholder="(11) 99245-8774"
            aria-invalid
            :state='phoneIsValid'
            @keyup="validatePhone($event)"
            v-mask="['(##) #####-####']">
          </b-form-input>
        </div>
        <b-form-invalid-feedback :state='phoneIsValid'>
          {{getError(v$.billingData.phone)}}
        </b-form-invalid-feedback>
      </div>
    </div>

    <div class="group" v-if="billingData.type === 'company'">
      <div>
        <label>Razão Social</label>
        <base-input-text
          block
          v-model="v$.billingData.companyName.$model"
          type="text"
          placeholder="Digite a razão social da empresa"
          :error="getError(v$.billingData.companyName)"
        />
      </div>
    </div>

    <div class="rows_2 mt-2" v-if="billingData.type === 'company'">
      <div>
        <label>CNPJ</label>
        <base-input-text
          class="w-100"
          v-model="v$.billingData.cnpj.$model"
          type="text"
          placeholder="Digite o CNPJ"
          v-mask="['##.###.###/####-##']"
          :error="getError(v$.billingData.cnpj)"
        />
      </div>

      <div>
        <label>Telefone (WhatsApp)</label>
        <div class="w-100">
          <b-form-input
            id="input-phone"
            v-model="v$.billingData.phone.$model"
            type="text"
            class='input-cpf w-100'
            style="height: 48px;"
            placeholder="(11) 99245-8774"
            aria-invalid
            :state='phoneIsValid'
            @keyup="validatePhone($event)"
            v-mask="['(##) #####-####']">
          </b-form-input>
          <b-form-invalid-feedback :state='phoneIsValid'>
            {{getError(v$.billingData.phone)}}
          </b-form-invalid-feedback>
        </div>
      </div>
    </div>

    <div class="rows_2" v-if="billingData.type === 'company'">
      <div>
        <label>Inscrição estadual (se houver)</label>
        <base-input-text
          v-model="v$.billingData.stateRegister.$model"
          type="text"
          placeholder="Digite a inscrição estadual"
          class="w-100"
          :error="getError(v$.billingData.stateRegister)"
        />
      </div>

      <div>
        <label>Inscrição municipal (se houver)</label>
        <base-input-text
          v-model="v$.billingData.cityRegister.$model"
          type="text"
          placeholder="Digite a inscrição municipal"
          class="w-100"
          :error="getError(v$.billingData.cityRegister)"
        />
      </div>
    </div>

    <div class="address mt-3" v-if="billingData.type">
      <h2><strong>Endereço:</strong></h2>
      <div class="cpf-data_nascimento_telefone">
        <div>
          <label>CEP</label>
          <div class="w-100 position-relative">
            <b-form-input
              id="input-cep"
              v-model="v$.billingData.address.cep.$model"
              type="text"
              class='form-control w-100'
              style="height: 48px;"
              placeholder="Digite o cep"
              @keyup="buscarCep($event)"
              :state="cepValido"
              aria-invalid
              v-mask="'##.###-###'">
            </b-form-input>
            <div>
              <b-spinner class='loading' v-if='checkingIfCEPExists' variant="primary" label="Spinning"></b-spinner>
            </div>
          </div>
          <b-form-invalid-feedback :state="cepValido">
            {{getError(v$.billingData.address.cep)}}
          </b-form-invalid-feedback>
        </div>

        <div>
          <label>Bairro</label>
          <base-input-text
            v-model="v$.billingData.address.neighborhood.$model"
            type="text"
            :disabled="!cepValido"
            placeholder="Digite o nome do bairro"
            class="w-100"
            :error="getError(v$.billingData.address.neighborhood)"
          />
        </div>

        <div>
          <label>Número</label>
          <base-input-text
            v-model="v$.billingData.address.number.$model"
            type="number"
            :disabled="!cepValido"
            placeholder="123"
            class="w-100"
            :error="getError(v$.billingData.address.number)"
          />
        </div>
      </div>

      <div class="rows_2 mt-2">
        <div>
          <label>Rua</label>
          <base-input-text
            v-model="v$.billingData.address.street.$model"
            type="text"
            :disabled="!cepValido"
            placeholder="Digite o nome da rua ou avenida"
            class="w-100"
            :error="getError(v$.billingData.address.street)"
          />
        </div>

        <div>
          <label>Complemento (opcional)</label>
          <base-input-text
            v-model="v$.billingData.address.complement.$model"
            type="text"
            :disabled="!cepValido"
            placeholder="Digite o complemento"
            class="w-100"
            :error="getError(v$.billingData.address.complement)"
          />
        </div>
      </div>

      <div class="rows_2 mt-2">
        <div>
          <label>Cidade</label>
          <base-input-text
            v-model="v$.billingData.address.city.$model"
            type="text"
            placeholder="Digite o nome da cidade"
            class="w-100"
            :error="getError(v$.billingData.address.city)"
          />
        </div>

        <div>
          <label>Estado</label>
          <base-input-select
            class="w-100"
            v-model="v$.billingData.address.state.$model"
            placeholder="Selecione um estado"
            :options="stateOptions"
            :error="getError(v$.billingData.address.state)"
            _key="key"
            _value="value"
          />
        </div>
      </div>
    </div>

    <div class='mt-3'>
      <b-alert
        show
        variant='danger'
        :class='{ hidden: !errors }'
      >
        {{ errors }}
      </b-alert>
    </div>

    <div class="buttons d-flex justify-content-between align-items-center">
      <base-button
        class='nav-btn'
        dark
        @click="$emit('back-step')"
      >
        Voltar
      </base-button>

      <b-overlay
        :show="loading"
        rounded
        opacity="0.6"
        spinner-small
        spinner-variant="primary"
        class="d-inline-block"
      >
        <base-button
          class='nav-btn'
          :disabled="disableButtonToNotBuyOutOfTimePlan"
          @click="next()"
        >
          Próximo
        </base-button>
      </b-overlay>
    </div>
  </div>
</template>

<script>
import useVuelidate from '@vuelidate/core'
import { required, requiredIf } from '@/utils/validators'
import { mask } from 'vue-the-mask'
import { mapActions, mapGetters } from 'vuex'
import { helpers} from '@vuelidate/validators'
import { CPF, CNPJ } from '../../../../utils/ValidateCPFCNPJ'
import { phoneValidator } from '../../../../utils/phoneValidator'
import { verifyCNPJExists, verifyCPFExists } from '../../../../services/Search_CPF_CNPJ_CEP'
import moment from 'moment/moment'

function validCNPJ () {
  return this.billingData.type === 'person' || this.CNPJIsValid
}
function validCEP () {
  return this.cepValido
}
function returnIfThePhoneIsCalid () {
  return this.phoneIsValid
}
export default {
  name: 'BillingData',
  setup () {
    return { v$: useVuelidate() }
  },
  props: {
    namePlan: { type: String},
  },
  data(){
    return {
      loading: false,
      disableButtonToNotBuyOutOfTimePlan: true,
      plans: {},
      cepValido: null,
      CNPJIsValid: null,
      cpfIsValid: null,
      phoneIsValid: null,
      birthDateIsValid: null,
      checkingIfCPFExists: false,
      checkingIfCNPJExists: false,
      checkingIfCEPExists: false,
      birthDate: '',
      document: '',
      plan: {},
      typeOptions: [
        { key: 'person', value: 'PESSOA FÍSICA (CPF)'},
        { key: 'company', value: 'PESSOA JURÍDICA (CNPJ)'},
      ],
      stateOptions: [
        { key: "Acre" , value: "Acre (AC)" },
        { key: "Alagoas" , value: "Alagoas (AL)" },
        { key: "Amapá" , value: "Amapá (AP)" },
        { key: "Amazonas" , value: "Amazonas (AM)" },
        { key: "Bahia" , value: "Bahia (BA)" },
        { key: "Ceará" , value: "Ceará (CE)" },
        { key: "Distrito Federal" , value: "Distrito Federal (DF)" },
        { key: "Espírito Santo" , value: "Espírito Santo (ES)" },
        { key: "Goiás" , value: "Goiás (GO)" },
        { key: "Maranhão" , value: "Maranhão (MA)" },
        { key: "Mato Grosso " , value: "Mato Grosso (MT)" },
        { key: "Mato Grosso do Sul" , value: "Mato Grosso do Sul (MS)" },
        { key: "Minas Gerais" , value: "Minas Gerais (MG)" },
        { key: "Pará" , value: "Pará (PA)" },
        { key: "Paraíba" , value: "Paraíba (PB)" },
        { key: "Paraná" , value: "Paraná (PR)" },
        { key: "Pernambuco" , value: "Pernambuco (PE)" },
        { key: "Piauí" , value: "Piauí (PI)" },
        { key: "Rio de Janeiro" , value: "Rio de Janeiro (RJ)" },
        { key: "Rio Grande do Norte" , value: "Rio Grande do Norte (RN)" },
        { key: "Rio Grande do Sul" , value: "Rio Grande do Sul (RS)" },
        { key: "Rondônia" , value: "Rondônia (RO)" },
        { key: "Roraima" , value: "Roraima (RR)" },
        { key: "Santa Catarina" , value: "Santa Catarina (SC)" },
        { key: "São Paulo" , value: "São Paulo (SP)" },
        { key: "Sergipe" , value: "Sergipe (SE)" },
        { key: "Tocantins" , value: "Tocantins (TO)" },
      ],
    }
  },
  directives: { mask },
  computed: {
    ...mapGetters('user', {
      user: 'current',
    }),
    ...mapGetters('checkout2', {
      billingData: 'getBillingData',
      getSteps: 'getSteps',
    }),
    ...mapGetters('plans', {
      getPlans: 'getPlans'
    }),
    errors() {
      return this.$store.getters['access/error']
    }
  },
  validations () {
    return {
      billingData: {
        type: {},
        name: { requiredIf: requiredIf(this.billingData.type === 'person') },
        birthDate: { requiredIf: requiredIf(this.billingData.type === 'person') },
        document: { requiredIf: requiredIf(this.billingData.type === 'person') },
        companyName: { requiredIf: requiredIf(this.billingData.type === 'company') },
        phone: { returnIfThePhoneIsCalid: helpers.withMessage('Telefone inválido', returnIfThePhoneIsCalid ) },
        cnpj: { requiredIf: requiredIf(this.billingData.type === 'company') },
        stateRegister: {},
        cityRegister: {},
        address: {
          street: { required },
          number: { required },
          complement: {},
          neighborhood: { required },
          cep: { validCEP: helpers.withMessage('CEP inválido, por favor digite um cep válido para prosseguir.', validCEP ) },
          city: { required },
          state: { required },
        },
      }
    }
  },
  methods: {
    ...mapActions('checkout2', {
      handleSetBillingData: 'handleSetBillingData',
      handleSetType: 'handleSetType',
    }),
    ...mapActions('userActionLogs', {
      createUserActionLogs: 'createUserActionLogs',
    }),
    async next() {
      try {
        this.loading = true;
        await this.$store.dispatch('access/updateError', ``)
        let validate = await this.v$.$validate()
        const isValidPhone = this.validatePhone();
        if(!isValidPhone){
          await this.$store.dispatch('access/updateError', `Telefone inválido`)
          this.loading = false;
          return
        }
        /*const cepIsvalid = await this.buscarCep();
        if(!cepIsvalid){
          await this.$store.dispatch('access/updateError', `CEP inválido, por favor digite um cep válido para prosseguir.`)
          this.loading = false;
          return
        }*/

        validate = await this.v$.$validate()
        if(!validate){
          await this.$store.dispatch('access/updateError', `Preencha todos os campos corretamente`)
          this.loading = false;
          return
        }

        if (this.billingData.type === 'person'){
          const isDateOfBirthValid = this.validateInformedDateOfBirth()
          if(!isDateOfBirthValid){
            await this.$store.dispatch('access/updateError', `Data de nascimento inválida!`)
            this.loading = false;
            return
          }

          const isValid = await this.buscarCPF();
          if(!isValid){
            await this.$store.dispatch('access/updateError', `CPF inválido`)
            this.loading = false;
            return
          }
        }

        if (this.billingData.type === 'company'){
          const isCNPJValid = await this.buscarCNPJ();
          if(!isCNPJValid){
            await this.$store.dispatch('access/updateError', `CNPJ inválido`)
            this.loading = false;
            return
          }
        }

        if (this.user && this.user.role !== 'admin') {
          const log = {
            action: 'PREENCHEU_DADOS_FATURAMENTO'
          }
          await this.createUserActionLogs(log)
        }

        await this.$store.dispatch('access/updateError', ``)
        this.loading = false;
        this.$emit('next-step')
      } catch (e) {
        await this.$store.dispatch('access/updateError', `Por favor, confira os dados informados e tente novamente.`)
        this.loading = false;
      }
    },

    clearFieldsSwitchBetweenLegalEntity(type){
      try {
        this.handleSetType(type)
        setTimeout(() => {
          this.billingData.companyName = ''
          this.billingData.cnpj = ''
          this.billingData.name = ''
          this.billingData.document = ''
          this.billingData.stateRegister = ''
          this.billingData.cityRegister = ''
          this.cpfIsValid = null
          this.CNPJIsValid = null
        }, 100)
      } catch (e) {
      }
    },

    async buscarCep(){
      try{
        const cepComApenasNumeros = this.billingData.address.cep.replace('.', '').replace('-', '')
        if(cepComApenasNumeros) {
          this.cepValido = null;
          if (cepComApenasNumeros.length > 7) {
            this.checkingIfCEPExists = true;
            const { data: response } = await new Promise((resolve, reject) => {
              this.$api.get(`/service/cep/${cepComApenasNumeros}`)
                .then((data) => {
                  return resolve(data)
                })
                .catch((error) => {
                  return resolve(error.response)
                })
            })
            this.checkingIfCEPExists = false;
            const { return: ok, message, result } = response
            this.cepValido = ok === "OK" && !message
            this.billingData.address.cep = this.billingData.address.cep;
            this.billingData.address.street = result && result.logradouro ? result.logradouro : this.billingData.address.street
            this.billingData.address.neighborhood = result && result.bairro ? result.bairro : this.billingData.address.neighborhood
            this.billingData.address.city = result && result.localidade? result.localidade : this.billingData.address.city
            this.billingData.address.complement = result && result.complemento? result.complemento : this.billingData.address.complement
            this.billingData.address.state = result && result.uf ? this.stateOptions.find((x) => x.value.indexOf(result.uf) > 0).key : this.billingData.address.state
            return this.cepValido
          }
          this.cepValido = false;
          return false;
        }
        this.cepValido = false;
        return  false;
      } catch (e) {
      }
    },

    async buscarCNPJ(){
      const {cnpj} = this.billingData || {}
      if(!cnpj){
        return false
      }

      const cnpjComApenasNumeros = cnpj.trim().replace(/[^\d]+/g, '')
      if (!cnpjComApenasNumeros) {
        return false
      }

      this.CNPJIsValid = null
      if (!CNPJ(cnpjComApenasNumeros)) {
        this.CNPJIsValid = false
        return false
      }


      try {
        this.checkingIfCNPJExists = true
        const { data } = await verifyCNPJExists(cnpjComApenasNumeros)
        this.checkingIfCNPJExists = false

        const { cnpjExists } = data || {}
        if (cnpjExists) {
          this.CNPJIsValid = cnpjExists
          return this.CNPJIsValid
        }
        return false
      } catch (e) {
        this.checkingIfCNPJExists = false
        this.CNPJIsValid = true
      }

      this.CNPJIsValid = true
      return true
    },

    async buscarCPF(){
      const cpfComApenasNumeros = this.billingData.document.replace(/[^\d]+/g, '')
      if(cpfComApenasNumeros && this.billingData.birthDate) {
        this.cpfIsValid = null;
        if(CPF(cpfComApenasNumeros)) {
          this.checkingIfCPFExists = true;
          //const { data: response } = await verifyCPFExists(cpfComApenasNumeros, this.billingData.birthDate)
          this.checkingIfCPFExists = false;
          //const { cpfExists } = response
          this.cpfIsValid = true
          return this.cpfIsValid
        }
        this.cpfIsValid = false;
        return false
      }
      return false;
    },

    validatePhone(){
      try {
        const phone = this.billingData.phone.replace(/[^\d]/g, '')
        this.phoneIsValid = phoneValidator(phone) && phone.length >= 10
        return this.phoneIsValid
      } catch (e) {
      }
    },

    validateInformedDateOfBirth() {
      const endDate = moment(new Date()).add(-18, 'year').toDate()
      const initialDate = moment(new Date(1900, 0, 1))
      const birthDate = moment(new Date(this.billingData.birthDate))
      return birthDate.isAfter(initialDate) && birthDate.isBefore(endDate) && this.billingData.birthDate
    },

    getError(value){
      if(value.$errors.length > 0){
        return value.$errors[0].$message
      }
      return ''
    },
    createHtmlInputCPF(){
      document.getElementById('input-cpf').addEventListener('keyup', async (e) => {
        this.billingData.document = e.target.value
        await this.buscarCPF()
      })
    }
  },
  async mounted() {
    if(this.namePlan !== 'PROMO'){
      this.disableButtonToNotBuyOutOfTimePlan = false;
    }
    this.plans = this.getPlans
    this.plan = this.plans[this.namePlan]
    const today = new Date(this.plan.today)
    const validFrom = new Date(this.plan.validFrom)
    const validUntil = new Date(this.plan.validUntil)
    if (today.getTime() >= validFrom.getTime() && today.getTime() <= validUntil.getTime()){
      this.disableButtonToNotBuyOutOfTimePlan = false;
    }
  },
  async created() {
    try {
      await this.$store.dispatch('access/updateError', ``)
      await this.buscarCep()
      if (this.user && this.user.username) {
        this.billingData.email = this.user.username
      }
      const { utm_source, utm_medium, utm_campaign, utm_id, utm_term, utm_content } = this.$route.query
      this.billingData.utm_source = utm_source ? utm_source : null
      this.billingData.utm_medium = utm_medium ? utm_medium : null
      this.billingData.utm_campaign = utm_campaign ? utm_campaign : null
      this.billingData.utm_id = utm_id ? utm_id : null
      this.billingData.utm_term = utm_term ? utm_term : null
      this.billingData.utm_content = utm_content ? utm_content : null
    } catch (e) {

    }
  }
}
</script>

<style lang="scss" scoped>

.loading{
  position: absolute;
  right: 5px;
  top: 10px;
  width: 1.5rem;
  height: 1.5rem;
}
.btn-radio{
  display: flex;
  flex-direction: row;
  .form-radio{
    margin-bottom: 10px;
    margin-right: 10px;
    cursor: pointer!important;
  }
}
.billing-data {
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    font-size: 14px;
    font-weight: 700;
  }

  .section-title {
    font-size: 18px;
    font-weight: 700;
    color: #212529;
  }

  .section-subtitle {
    font-size: 14px;
    color: #495057;
    margin: 0;
  }

  .billing-intro {
    font-size: 15px;
    color: #212529;
    margin: 0;
  }

  .usage-profile {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 24px;
    background: #f8f9fa;
    border-radius: 6px;

    .usage-descriptions {
      gap: 16px;

      p {
        width: 49%;
        min-width: 220px;
        margin: 0;
        font-size: 13px;
        color: #495057;
      }
    }

    .usage-divider {
      width: 100%;
      margin: 0;
      border: none;
      border-top: 1px solid #dee2e6;
    }

    .usage-note {
      padding: 12px 16px;
      background: #ecf0f3;
      border-radius: 4px;

      p {
        margin: 0;
        font-style: italic;
        font-size: 13px;
        color: #495057;
      }
    }
  }

  .buttons {
    margin-top: 24px;
  }

  .nav-btn {
    min-width: 140px;
    padding: 12px 20px;
  }
  .use-account-data {
    span {
      font-size: 18px;
    }
    .buttons {
      display: flex;
      gap: 8px;
    }
  }

  .address {
    display: flex;
    flex-direction: column;
    gap: 4px;
    h2 {
      font-size: 20px;
      font-weight: bold;
    }
    .group {
      display: grid;
      grid-template-columns:  60% 38%;
      gap: 2%;
    }
  }
  .hits_select {
    text-align: center;
    border: 1px solid #04A8F3;
    padding: 5px;
    border-radius: 6px;
    background: #CBD63E;
    color: #262626;
    font-weight: 700;
    width: 49%;
    font-size: 15px;
  }

  .hits_no_select {
    border: 1px solid #04A8F3;
    padding: 5px;
    border-radius: 6px;
    background: transparent;
    color: #04A8F3;
    font-weight: 700;
    width: 49%;
    font-size: 15px;
  }

  .cpf-data_nascimento_telefone{
    display: flex;
    justify-content: space-between;
    align-content: center;
    width: 100%;
    flex-wrap: wrap;
    div{
      width: 32%;
      min-width: 300px;
    }
  }

  .rows_2{
    display: flex;
    justify-content: space-between;
    align-content: center;
    width: 100%;
    flex-wrap: wrap;
    div{
      width: 49%;
      min-width: 300px;
    }
  }
}
</style>
